import {cn} from '@tempmee/core'
import {OutlineChevronDownIcon} from '@tempmee/react-icons'
import type React from 'react'
import {
  Children,
  Fragment,
  type ReactNode,
  forwardRef,
  isValidElement,
  useId,
  useRef,
  useState,
} from 'react'
import {Popover} from '../popover'
import {Tooltip} from '../tooltip'
import {MenuItemVariantsFactory} from './Variants'
import {SidebarCollapsedProvider, useSidebarCollapsed} from './collapsed'
import {type MenuColor, MenuColorProvider, useMenuColor} from './color'
import {useMenuDensity} from './density'
import {MenuSizeProvider, useMenuSize} from './size'

/** Any descendant `MenuItem` marked `selected` — flattens `<>…</>` fragments so
 * fragment-wrapped children are still inspected (depth 1 otherwise). */
function anySelected(node: ReactNode): boolean {
  return Children.toArray(node).some((child) => {
    if (!isValidElement(child)) return false
    const props = child.props as {selected?: boolean; children?: ReactNode}
    if (props.selected === true) return true
    return child.type === Fragment ? anySelected(props.children) : false
  })
}

interface ParentRowProps {
  icon?: ReactNode
  label: ReactNode
  href?: string
  onClick?: React.MouseEventHandler
  selected: boolean
  disabled: boolean
  rowClass: string
  /** Surface tone — tints the chevron (and its hover scrim) for the brand rail. */
  color: MenuColor
  open: boolean
  panelId: string
  /** Toggle the panel (the chevron, and the whole row when toggle-only). */
  onToggle: () => void
  /** Ensure the panel is open — fired when a *navigable* row is clicked, so
   * clicking into a section reveals its children (the chevron still collapses). */
  onOpen: () => void
}

/** The parent row — a navigable link/button with a separate chevron toggle, or a
 * single toggle button when it doesn't navigate. Extracted so the main component
 * stays simple. */
function ParentRow({
  icon,
  label,
  href,
  onClick,
  selected,
  disabled,
  rowClass,
  color,
  open,
  panelId,
  onToggle,
  onOpen,
}: ParentRowProps) {
  const dark = color === 'dark'
  const iconEl = icon && (
    <span className="flex size-6 shrink-0 items-center justify-center">{icon}</span>
  )
  const labelEl = <span className="min-w-0 flex-1 truncate text-left">{label}</span>
  const chevron = (
    <OutlineChevronDownIcon
      className={cn('size-5 shrink-0 transition-transform duration-200', open && 'rotate-180')}
      aria-hidden
    />
  )
  const chevronTone = dark ? 'text-white/70' : 'text-text-secondary'
  const toggleAria = {'aria-expanded': open, 'aria-controls': panelId} as const

  // Toggle-only: the whole row expands/collapses; it never navigates. The chevron
  // sits in an overlaid (click-through) span — same as the navigable branch — so it
  // isn't caught by the row's `[&_svg]:size-6` icon rule (which would bloat it to 24px).
  if (href == null && onClick == null) {
    return (
      <div className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={onToggle}
          {...toggleAria}
          className={cn(rowClass, 'pr-10')}
        >
          {iconEl}
          {labelEl}
        </button>
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute top-1/2 right-1 flex size-7 -translate-y-1/2 items-center justify-center',
            chevronTone,
          )}
        >
          {chevron}
        </span>
      </div>
    )
  }

  // Navigable: the row navigates AND opens the panel (so clicking into a section
  // reveals its children); the chevron toggles, i.e. it's how you collapse.
  const navProps = {
    'aria-current': selected ? ('page' as const) : undefined,
    className: cn(rowClass, 'pr-10'),
  }
  return (
    <div className="relative">
      {href != null ? (
        <a
          href={disabled ? undefined : href}
          aria-disabled={disabled || undefined}
          onClick={onOpen}
          {...navProps}
        >
          {iconEl}
          {labelEl}
        </a>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={(event) => {
            onClick?.(event)
            onOpen()
          }}
          {...navProps}
        >
          {iconEl}
          {labelEl}
        </button>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation()
          onToggle()
        }}
        {...toggleAria}
        aria-label={typeof label === 'string' ? `Toggle ${label}` : 'Toggle section'}
        className={cn(
          'absolute top-1/2 right-1 flex size-7 -translate-y-1/2 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset',
          chevronTone,
          dark ? 'hover:bg-white/10' : 'hover:bg-action-hover',
        )}
      >
        {chevron}
      </button>
    </div>
  )
}

interface CollapsibleMenuItemBaseProps {
  /** Leading icon (24×24), matching `MenuItem`. */
  icon?: ReactNode
  /** Parent row label (the child `MenuItem`s go in `children`). */
  label: ReactNode
  /** The parent's OWN route is active — styles the row like a selected `MenuItem`. */
  selected?: boolean
  disabled?: boolean
  /** Compact 32px row (matches `MenuItem`'s `dense`). Inherits from `Sidebar
   * dense` / `MenuDensityProvider` when omitted, else `false`. */
  dense?: boolean
  /** Taller 48px touch-target row (matches `MenuItem`'s `smallScreen`). @default false */
  smallScreen?: boolean
  /** Initial open state (uncontrolled). Falls back to auto-open when a child is `selected`. */
  defaultOpen?: boolean
  /** Controlled open state. */
  open?: boolean
  /** Fires when the panel is toggled. */
  onOpenChange?: (open: boolean) => void
  className?: string
  /** The nested `MenuItem`s revealed when expanded. */
  children: ReactNode
}

export interface CollapsibleMenuItemProps extends CollapsibleMenuItemBaseProps {
  /** Present → the parent is a navigable link. */
  href?: string
  /** Present → the parent is a navigable button (SPA nav). Omit both `href` and
   * `onClick` for a toggle-only group header that never navigates. */
  onClick?: React.MouseEventHandler
}

/**
 * The collapsed (mini-rail) form of a `CollapsibleMenuItem`: an icon-only button
 * that opens a flyout `Popover` holding the children on click. Inside the flyout
 * `collapsed` is reset to `false`, so the children (and the navigable-parent
 * header) render with full labels — and `color` is reset to `light`, because the
 * `Popover` is a white surface even when the rail behind it is the brand teal.
 * A navigable parent gets a clickable header row; a toggle-only parent shows just
 * its label as a caption.
 */
function CollapsedParent({
  icon,
  label,
  href,
  onClick,
  selected,
  disabled,
  dense,
  color,
  hasSelectedChild,
  children,
}: {
  icon?: ReactNode
  label: ReactNode
  href?: string
  onClick?: React.MouseEventHandler
  selected: boolean
  disabled: boolean
  dense: boolean
  color: MenuColor
  hasSelectedChild: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  // The flyout anchors to a wrapper span (not the button) because `Tooltip` clones
  // its child and takes over its ref — so the button can't also hold the anchor ref.
  const anchorRef = useRef<HTMLSpanElement>(null)
  const navigable = href != null || onClick != null
  const ariaLabel = typeof label === 'string' ? label : undefined
  // Icon-only trigger; tint it when a (hidden) child route is active — light rail
  // only, for the same reason as the inline parent row (see below).
  const rowClass = cn(
    MenuItemVariantsFactory({color, selected, disabled, dense}),
    hasSelectedChild && !selected && color === 'light' && 'text-primary',
  )
  // The flyout is a white `Popover` surface — its header always uses the light tone.
  const headerClass = cn(MenuItemVariantsFactory({selected}), 'w-full')
  const headerContent = (
    <>
      {icon && <span className="flex size-6 shrink-0 items-center justify-center">{icon}</span>}
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
    </>
  )

  return (
    <>
      {/* `anchorRef` sits on the span (the flyout anchor); the label surfaces as a
          right-anchored tooltip on hover, suppressed while the flyout is open. */}
      <span ref={anchorRef} className="block">
        <Tooltip title={label} placement="right" disabled={open}>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={ariaLabel}
            className={cn(rowClass, 'w-full justify-center')}
          >
            {icon && (
              <span className="flex size-6 shrink-0 items-center justify-center">{icon}</span>
            )}
          </button>
        </Tooltip>
      </span>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        placement="right-start"
        // A flyout off the brand rail is a menu, so it drops the teal hairline for
        // the elevated `Menu` treatment. A light rail keeps the authored default.
        surface={color === 'dark' ? 'menu' : 'default'}
      >
        {/* Reset `collapsed` so the flyout's items render with full labels, and
            `color` because the popover surface is white even on a dark rail. */}
        <SidebarCollapsedProvider value={false}>
          <MenuColorProvider value="light">
            {/* The `Popover` is the surface (bordered, padded, elevated) — this is
              just the menu layout inside it. Any click dismisses the flyout (the
              chosen row then navigates). */}
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: closes the flyout on child selection; the children are the interactive targets and remain keyboard-operable */}
            <div
              role="menu"
              aria-label={ariaLabel}
              onClick={() => setOpen(false)}
              className="flex flex-col gap-0.5"
            >
              {navigable ? (
                href != null ? (
                  <a href={disabled ? undefined : href} role="menuitem" className={headerClass}>
                    {headerContent}
                  </a>
                ) : (
                  <button type="button" role="menuitem" onClick={onClick} className={headerClass}>
                    {headerContent}
                  </button>
                )
              ) : (
                // Toggle-only parent: a non-interactive section heading — same
                // icon + label as the navigable header, but muted/bold and not a link.
                <div
                  className={cn(
                    headerClass,
                    'cursor-default font-bold text-text-secondary hover:bg-transparent',
                  )}
                >
                  {headerContent}
                </div>
              )}
              {children}
            </div>
          </MenuColorProvider>
        </SidebarCollapsedProvider>
      </Popover>
    </>
  )
}

/** The indented child panel — a `grid-template-rows` 0fr↔1fr transition gives a
 * JS-free height animation; `inert` when closed so the hidden children aren't
 * focusable/interactive.
 *
 * Nesting reads from the 24px indent alone — there is deliberately no tree/guide
 * rule down the left edge (per Figma Admin-Panel v3, where child rows are inset
 * 24px from the parent's left edge); indentation *is* the child affordance. */
function CollapsePanel({
  open,
  panelId,
  label,
  children,
}: {
  open: boolean
  panelId: string
  label: ReactNode
  children: ReactNode
}) {
  return (
    <div
      className={cn(
        'grid transition-[grid-template-rows] duration-200 ease-out',
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
      )}
    >
      <div className="overflow-hidden" inert={!open}>
        <div
          id={panelId}
          role="menu"
          aria-label={typeof label === 'string' ? label : undefined}
          className="mt-1 ml-6 flex flex-col gap-1"
        >
          {/* Nested rows are always the 36px `md` height — on an `lg` rail that
              gives Figma's 48px parent / 36px child pairing. */}
          <MenuSizeProvider value="md">{children}</MenuSizeProvider>
        </div>
      </div>
    </div>
  )
}

/**
 * CollapsibleMenuItem — a nested (expandable) sidebar nav item: a parent row
 * (icon + label + rotating chevron) over a smoothly-collapsing panel of indented
 * child `MenuItem`s. The DS take on MUI's "nested list" pattern.
 *
 * Two shapes, keyed on whether it navigates:
 * - **Navigable** (`href` or `onClick`): clicking the row navigates AND opens the
 *   panel (so clicking into a section reveals its children); the chevron collapses it.
 * - **Toggle-only** (neither): the whole row is a button that just expands/collapses.
 *
 * Multi-open (each independent), auto-opens the branch whose child is `selected`
 * (so the active route shows on load), depth 1. Uncontrolled by default; pass
 * `open`/`onOpenChange` to control it.
 *
 * @experimental Shipped ahead of design review; API/visuals may change.
 */
export const CollapsibleMenuItem = forwardRef<HTMLDivElement, CollapsibleMenuItemProps>(
  (
    {
      icon,
      label,
      href,
      onClick,
      selected = false,
      disabled = false,
      dense: denseProp,
      smallScreen = false,
      defaultOpen,
      open: controlledOpen,
      onOpenChange,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const panelId = useId()
    // Explicit `dense` wins; otherwise inherit from `Sidebar dense` / provider.
    const inheritedDense = useMenuDensity()
    const dense = denseProp ?? inheritedDense
    // Collapsed (mini) rail → icon-only parent + flyout popover for the children.
    const collapsed = useSidebarCollapsed()
    // Surface tone — `dark` (the brand rail) renders white rows + chevron.
    const color = useMenuColor()
    // The parent row sits at the rail's own height; the panel resets children to `md`.
    const size = useMenuSize()
    // Auto-open the branch that holds the active route (any child `selected`).
    const hasSelectedChild = anySelected(children)
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen ?? hasSelectedChild)
    const open = controlledOpen ?? uncontrolledOpen
    const setOpen = (next: boolean) => {
      if (controlledOpen === undefined) setUncontrolledOpen(next)
      onOpenChange?.(next)
    }

    // Mini rail: the inline panel is replaced by an icon-only button + flyout.
    if (collapsed) {
      return (
        <div ref={ref} className={cn('flex flex-col', className)} {...rest}>
          <CollapsedParent
            icon={icon}
            label={label}
            href={href}
            onClick={onClick}
            selected={selected}
            disabled={disabled}
            dense={dense}
            color={color}
            hasSelectedChild={hasSelectedChild}
          >
            {children}
          </CollapsedParent>
        </div>
      )
    }

    // Subtle "contains the active route" hint while the branch is collapsed shut.
    // Only on a light rail: the teal reads as a washed-out blue against the dark
    // brand teal, so a dark rail keeps the row white like its siblings. Low cost —
    // the branch holding the active route auto-opens, so this state only appears
    // once someone deliberately collapses it.
    const containsActive = !open && hasSelectedChild && !selected && color === 'light'
    const rowClass = cn(
      MenuItemVariantsFactory({color, size, selected, disabled, dense, smallScreen}),
      containsActive && 'text-primary',
    )

    return (
      <div ref={ref} className={cn('flex flex-col', className)} {...rest}>
        <ParentRow
          icon={icon}
          label={label}
          href={href}
          onClick={onClick}
          selected={selected}
          disabled={disabled}
          rowClass={rowClass}
          color={color}
          open={open}
          panelId={panelId}
          onToggle={() => setOpen(!open)}
          onOpen={() => setOpen(true)}
        />
        <CollapsePanel open={open} panelId={panelId} label={label}>
          {children}
        </CollapsePanel>
      </div>
    )
  },
)

CollapsibleMenuItem.displayName = 'CollapsibleMenuItem'
